import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { PERMISSION_GROUPS, ALL_PERMISSIONS } from '../../config/permissions';
import PasswordInput from '../../components/common/PasswordInput';

export default function AdminRolesPermissions() {
  const [roles, setRoles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('roles');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [roleForm, setRoleForm] = useState({ name: '', description: '', permissions: [], is_active: true });
  const [assignForm, setAssignForm] = useState({ user: '', role: '' });

  // Create User
  const [userForm, setUserForm] = useState({ email: '', password: '', first_name: '', last_name: '' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);

  // Admin users for assignment
  const [adminUsers, setAdminUsers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [selectedAssignMember, setSelectedAssignMember] = useState(null);
  const memberDropdownRef = useRef(null);

  const fetchData = () => {
    Promise.all([
      api.get('/admin/roles/'),
      api.get('/admin/role-assignments/'),
      api.get('/accounts/admin/users/'),
    ]).then(([rolesRes, assignRes, usersRes]) => {
      setRoles(rolesRes.data.results || rolesRes.data);
      setAssignments(assignRes.data.results || assignRes.data);
      setAdminUsers(usersRes.data.results || usersRes.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  // Close member dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(e.target)) setShowMemberDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredSearchMembers = adminUsers.filter(m => {
    const q = memberSearch.toLowerCase();
    const name = (m.full_name || '').toLowerCase();
    const email = (m.email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  // ─── Roles ───

  const resetRoleForm = () => {
    setRoleForm({ name: '', description: '', permissions: [], is_active: true });
    setEditingId(null);
    setShowForm(false);
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const res = await api.put(`/admin/roles/${editingId}/`, roleForm);
        setRoles(roles.map(r => r.id === editingId ? res.data : r));
        toast.success('Role updated!');
      } else {
        const res = await api.post('/admin/roles/', roleForm);
        setRoles([...roles, res.data]);
        toast.success('Role created!');
      }
      resetRoleForm();
    } catch { toast.error('Failed to save role.'); }
  };

  const handleEditRole = (role) => {
    setRoleForm({ name: role.name, description: role.description || '', permissions: role.permissions || [], is_active: role.is_active });
    setEditingId(role.id);
    setShowForm(true);
    setActiveTab('roles');
  };

  const handleDeleteRole = async (id) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await api.delete(`/admin/roles/${id}/`);
      setRoles(roles.filter(r => r.id !== id));
      toast.success('Role deleted.');
    } catch { toast.error('Delete failed.'); }
  };

  const togglePermission = (perm) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  // ─── Assignments ───

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!assignForm.user || !assignForm.role) {
      toast.error('Please select both a user and a role.');
      return;
    }
    try {
      const res = await api.post('/admin/role-assignments/', assignForm);
      setAssignments([...assignments, res.data]);
      setAssignForm({ user: '', role: '' });
      setSelectedAssignMember(null);
      setMemberSearch('');
      setShowForm(false);
      toast.success('Role assigned!');
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        const msg = typeof data === 'string' ? data
          : data.detail || data.non_field_errors?.[0]
          || Object.values(data).flat().join(' ');
        toast.error(msg || 'Failed to assign role.');
      } else { toast.error('Failed to assign role.'); }
    }
  };

  const handleRemoveAssignment = async (id) => {
    if (!window.confirm('Remove this role assignment?')) return;
    try {
      await api.delete(`/admin/role-assignments/${id}/`);
      setAssignments(assignments.filter(a => a.id !== id));
      toast.success('Assignment removed.');
    } catch { toast.error('Remove failed.'); }
  };

  // ─── Create User ───

  const resetUserForm = () => { setUserForm({ email: '', password: '', first_name: '', last_name: '' }); setEditingUserId(null); };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!userForm.first_name || !userForm.last_name) {
      toast.error('First and last name are required.'); return;
    }
    if (editingUserId) {
      // Update flow: email and password optional
      if (userForm.password && userForm.password.length < 8) {
        toast.error('Password must be at least 8 characters.'); return;
      }
      setCreatingUser(true);
      try {
        const payload = { first_name: userForm.first_name, last_name: userForm.last_name };
        if (userForm.email) payload.email = userForm.email;
        if (userForm.password) payload.password = userForm.password;
        await api.patch(`/accounts/admin/users/${editingUserId}/`, payload);
        toast.success('User updated successfully!');
        resetUserForm();
        fetchData();
      } catch (err) {
        const data = err.response?.data;
        const msg = data ? Object.values(data).flat().join(' ') : 'Failed to update user.';
        toast.error(msg);
      } finally { setCreatingUser(false); }
      return;
    }
    if (!userForm.email || !userForm.password) {
      toast.error('Email and password are required.'); return;
    }
    if (userForm.password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    setCreatingUser(true);
    try {
      await api.post('/accounts/admin/users/create/', userForm);
      toast.success('Admin user created successfully!');
      resetUserForm();
      fetchData();
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        const msg = Object.values(data).flat().join(' ');
        toast.error(msg || 'Failed to create user.');
      } else { toast.error('Failed to create user.'); }
    } finally { setCreatingUser(false); }
  };

  const handleEditUser = (u) => {
    setEditingUserId(u.id);
    setUserForm({ email: u.email || '', password: '', first_name: u.first_name || '', last_name: u.last_name || '' });
    setViewingUser(null);
  };

  const handleDeleteUser = async (u) => {
    if (u.is_superuser) { toast.error('Superusers cannot be deleted.'); return; }
    if (!window.confirm(`Delete admin user "${u.full_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/accounts/admin/users/${u.id}/`);
      toast.success('User deleted.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed.');
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.post(`/accounts/admin/users/${u.id}/toggle-active/`);
      toast.success(`User ${u.is_active ? 'deactivated' : 'activated'}.`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Roles & Permissions</h1>
        {activeTab !== 'create user' && (
          <button onClick={() => { resetRoleForm(); setAssignForm({ user: '', role: '' }); setSelectedAssignMember(null); setMemberSearch(''); setShowForm(!showForm); }}
            className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition">
            {showForm ? 'Cancel' : activeTab === 'roles' ? 'Create Role' : 'Assign Role'}
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        {['roles', 'assignments', 'create user'].map(tab => (
          <button key={tab} onClick={() => { setActiveTab(tab); setShowForm(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition capitalize ${
              activeTab === tab ? 'bg-[#0B5D1E] text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'
            }`}>{tab}</button>
        ))}
      </div>

      {activeTab === 'roles' && (
        <>
          {showForm && (
            <form onSubmit={handleRoleSubmit} className="bg-white rounded-xl shadow-sm border p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Role Name</label>
                  <input type="text" required value={roleForm.name}
                    onChange={e => setRoleForm({ ...roleForm, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input type="checkbox" id="role_active" checked={roleForm.is_active}
                    onChange={e => setRoleForm({ ...roleForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-[#0B5D1E] rounded" />
                  <label htmlFor="role_active" className="text-sm font-medium text-gray-700">Active</label>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
                  <textarea value={roleForm.description} onChange={e => setRoleForm({ ...roleForm, description: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" rows={2} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500 mb-2">Permissions</label>
                  <div className="space-y-4">
                    {/* Select All / Deselect All */}
                    <div className="flex gap-2 mb-2">
                      <button type="button" onClick={() => setRoleForm({ ...roleForm, permissions: [...ALL_PERMISSIONS] })}
                        className="text-xs px-3 py-1 rounded bg-[#0B5D1E] text-white hover:bg-[#062E10] transition">Select All</button>
                      <button type="button" onClick={() => setRoleForm({ ...roleForm, permissions: [] })}
                        className="text-xs px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 transition">Deselect All</button>
                    </div>
                    {PERMISSION_GROUPS.map(group => (
                      <div key={group.label} className="border rounded-lg p-3">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">{group.label}</h4>
                        <div className="flex flex-wrap gap-2">
                          {group.permissions.map(perm => (
                            <button key={perm} type="button" onClick={() => togglePermission(perm)}
                              className={`text-xs px-3 py-1.5 rounded-full font-medium transition ${
                                roleForm.permissions.includes(perm) ? 'bg-[#0B5D1E] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}>
                              {perm.replace(/_/g, ' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button type="submit" className="mt-4 bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold">
                {editingId ? 'Update' : 'Create'}
              </button>
            </form>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loading ? (
              <div className="col-span-2 text-center py-12 text-gray-500">Loading...</div>
            ) : roles.length === 0 ? (
              <div className="col-span-2 text-center py-12 text-gray-400">No roles defined.</div>
            ) : roles.map(r => (
              <div key={r.id} className="bg-white rounded-xl shadow-sm border p-5">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{r.name}</h3>
                    <span className={`w-2 h-2 rounded-full ${r.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEditRole(r)} className="text-blue-600 hover:underline text-sm">Edit</button>
                    <button onClick={() => handleDeleteRole(r.id)} className="text-red-600 hover:underline text-sm">Delete</button>
                  </div>
                </div>
                {r.description && <p className="text-sm text-gray-500 mb-2">{r.description}</p>}
                <div className="flex flex-wrap gap-1">
                  {(r.permissions || []).map(p => (
                    <span key={p} className="text-xs bg-[#0B5D1E]/10 text-[#0B5D1E] px-2 py-0.5 rounded">
                      {p.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">{r.assignment_count || 0} user(s) assigned</p>
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === 'assignments' && (
        <>
          {showForm && (
            <form onSubmit={handleAssignSubmit} className="bg-white rounded-xl shadow-sm border p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div ref={memberDropdownRef}>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Select Member</label>
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={memberSearch}
                      onChange={(e) => { setMemberSearch(e.target.value); setShowMemberDropdown(true); if (selectedAssignMember) { setSelectedAssignMember(null); setAssignForm({ ...assignForm, user: '' }); } }}
                      onFocus={() => setShowMemberDropdown(true)}
                      placeholder="Type name, email, or membership number..."
                      className="w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                    />
                    {selectedAssignMember && (
                      <button type="button" onClick={() => { setSelectedAssignMember(null); setMemberSearch(''); setAssignForm({ ...assignForm, user: '' }); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                    {showMemberDropdown && memberSearch && !selectedAssignMember && (
                      <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredSearchMembers.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-400">No members found</div>
                        ) : filteredSearchMembers.slice(0, 10).map(m => (
                          <button type="button" key={m.id} onClick={() => {
                            setSelectedAssignMember(m);
                            setMemberSearch(`${m.full_name || ''} — ${m.email || ''}`);
                            setAssignForm({ ...assignForm, user: m.id });
                            setShowMemberDropdown(false);
                          }}
                            className="w-full text-left px-4 py-2.5 hover:bg-green-50 flex justify-between items-center border-b last:border-b-0">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{m.full_name}</div>
                              <div className="text-xs text-gray-500">{m.email}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedAssignMember && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      <span><strong>{selectedAssignMember.full_name}</strong> — {selectedAssignMember.email}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Role</label>
                  <select required value={assignForm.role} onChange={e => setAssignForm({ ...assignForm, role: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
                    <option value="">Select Role...</option>
                    {roles.filter(r => r.is_active).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" className="mt-4 bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold">Assign</button>
            </form>
          )}

          <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">User</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Role</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Assigned</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-8 text-gray-500">Loading...</td></tr>
                ) : assignments.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-8 text-gray-400">No role assignments.</td></tr>
                ) : assignments.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm">{a.user_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{a.user_email}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs bg-[#0B5D1E]/10 text-[#0B5D1E] px-2 py-1 rounded font-semibold">{a.role_name}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(a.assigned_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <button onClick={() => handleRemoveAssignment(a.id)} className="text-red-600 hover:underline text-sm">Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {activeTab === 'create user' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border p-6 max-w-lg">
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              {editingUserId ? 'Edit Admin User' : 'Create Admin User'}
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              {editingUserId
                ? 'Update the admin account details. Leave password blank to keep the current password.'
                : 'Create a new admin account with email and password. You can then assign a role from the Assignments tab.'}
            </p>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input type="text" required value={userForm.first_name}
                    onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input type="text" required value={userForm.last_name}
                    onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Email Address {!editingUserId && <span className="text-red-500">*</span>}
                </label>
                <input type="email" required={!editingUserId} value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Password {!editingUserId && <span className="text-red-500">*</span>}
                </label>
                <PasswordInput required={!editingUserId} value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder={editingUserId ? 'Leave blank to keep current password' : 'Min. 8 characters'}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={creatingUser}
                  className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#062E10] transition disabled:opacity-50">
                  {creatingUser ? (editingUserId ? 'Updating...' : 'Creating...') : (editingUserId ? 'Update User' : 'Create User')}
                </button>
                <button type="button" onClick={resetUserForm}
                  className="px-6 py-2 rounded-lg font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition">
                  {editingUserId ? 'Cancel' : 'Reset'}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">Admin Users</h3>
              <span className="text-xs text-gray-500">{adminUsers.length} user(s)</span>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-8 text-gray-500">Loading...</td></tr>
                ) : adminUsers.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-8 text-gray-400">No admin users yet.</td></tr>
                ) : adminUsers.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{u.full_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{u.email || '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                        u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${u.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <div className="flex flex-wrap gap-1">
                        {u.is_superuser && (
                          <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-semibold">Superuser</span>
                        )}
                        {(u.assigned_roles || []).length > 0 ? (
                          (u.assigned_roles || []).map((r) => {
                            const isSuperAdmin = r.toLowerCase().replace(/[\s_-]/g, '') === 'superadmin';
                            return (
                              <span
                                key={r}
                                className={`px-2 py-0.5 rounded font-semibold ${
                                  isSuperAdmin
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {r}
                              </span>
                            );
                          })
                        ) : (
                          !u.is_superuser && (
                            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-semibold">Admin</span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3 text-sm">
                        <button onClick={() => setViewingUser(u)}
                          className="text-gray-600 hover:text-gray-900 hover:underline">View</button>
                        <button onClick={() => handleEditUser(u)}
                          className="text-blue-600 hover:underline">Edit</button>
                        <button onClick={() => handleToggleActive(u)}
                          className={u.is_active ? 'text-orange-600 hover:underline' : 'text-green-600 hover:underline'}>
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => handleDeleteUser(u)} disabled={u.is_superuser}
                          className="text-red-600 hover:underline disabled:opacity-40 disabled:cursor-not-allowed">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {viewingUser && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setViewingUser(null)}>
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-bold text-gray-900">User Details</h3>
                  <button onClick={() => setViewingUser(null)} className="text-gray-400 hover:text-gray-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <dl className="space-y-3 text-sm">
                  <div><dt className="text-gray-500">Full Name</dt><dd className="font-medium text-gray-900">{viewingUser.full_name}</dd></div>
                  <div><dt className="text-gray-500">Email</dt><dd className="font-medium text-gray-900">{viewingUser.email || '—'}</dd></div>
                  <div><dt className="text-gray-500">Status</dt><dd className="font-medium">{viewingUser.is_active ? 'Active' : 'Inactive'}</dd></div>
                  <div><dt className="text-gray-500">Type</dt><dd className="font-medium">{viewingUser.is_superuser ? 'Superuser' : 'Admin'}</dd></div>
                  <div>
                    <dt className="text-gray-500">Assigned Roles</dt>
                    <dd className="font-medium">
                      {(viewingUser.assigned_roles || []).length > 0
                        ? (viewingUser.assigned_roles || []).join(', ')
                        : '—'}
                    </dd>
                  </div>
                  {viewingUser.date_joined && (
                    <div><dt className="text-gray-500">Joined</dt><dd className="font-medium">{new Date(viewingUser.date_joined).toLocaleString()}</dd></div>
                  )}
                  {viewingUser.last_login && (
                    <div><dt className="text-gray-500">Last Login</dt><dd className="font-medium">{new Date(viewingUser.last_login).toLocaleString()}</dd></div>
                  )}
                </dl>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
